// Pure matching for rt-combobox (components/rt-combobox); the component owns focus, keys and the popup.

export interface ComboOption {
  readonly id: number | string
  readonly name: string
  /** Small grey text after the name, e.g. "Midland, TX". */
  readonly sub?: string
}

export interface ComboRow {
  readonly option: ComboOption
  /** The name split around the match so the template can highlight it without innerHTML. */
  readonly pre: string
  readonly hit: string
  readonly post: string
}

/** Case-insensitive "anywhere in the name" filter; an empty query lists everything. */
export function filterOptions(options: readonly ComboOption[], query: string): ComboRow[] {
  const q = query.trim().toLowerCase()
  const rows: ComboRow[] = []
  for (const option of options) {
    const i = q ? option.name.toLowerCase().indexOf(q) : -1
    if (q && i < 0) continue
    rows.push(i < 0
      ? { option, pre: option.name, hit: '', post: '' }
      : { option, pre: option.name.slice(0, i), hit: option.name.slice(i, i + q.length), post: option.name.slice(i + q.length) })
  }
  return rows
}

/** True when the typed text matches one option name exactly (ignoring case and outer spaces). */
export function exactMatch(options: readonly ComboOption[], text: string): ComboOption | undefined {
  const t = text.trim().toLowerCase()
  return t ? options.find(o => o.name.trim().toLowerCase() === t) : undefined
}

/** Same ids and names in the same order (the arrays may be different objects). */
export function sameOptions(a: readonly ComboOption[] | null | undefined, b: readonly ComboOption[] | null | undefined): boolean {
  if (a === b) return true
  if (!a || !b || a.length !== b.length) return false
  return a.every((o, i) => o.id === b[i].id && o.name === b[i].name && o.sub === b[i].sub)
}
